import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import LedgerIcon from '@interxfi/design-system/icons/react/icon-ledger';
import type { IxcPortfolioV1BooksControls } from '@interxfi/types/generated/api';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';

export const programControlsBooksFormSchema = z.object({
  canCreditCustomers: z.boolean(),
  canDebitCustomers: z.boolean(),
  canTradeUnsettledCash: z.boolean(),
  canCreditPlatformAccount: z.boolean(),
  canDebitPlatformAccounts: z.boolean(),
});

export type ProgramControlsBooksFormValues = z.infer<typeof programControlsBooksFormSchema>;

export const programControlsBooksFormDefaultValues: ProgramControlsBooksFormValues = {
  canCreditCustomers: false,
  canDebitCustomers: false,
  canTradeUnsettledCash: false,
  canCreditPlatformAccount: false,
  canDebitPlatformAccounts: false,
};

export function getInitialFormValuesForProgramBooksControls(data: IxcPortfolioV1BooksControls | undefined): ProgramControlsBooksFormValues {
  if (!data) {
    return programControlsBooksFormDefaultValues;
  }

  return {
    canCreditCustomers: Boolean(data.settings?.canCreditCustomers),
    canDebitCustomers: Boolean(data.settings?.canDebitCustomers),
    canTradeUnsettledCash: Boolean(data.settings?.canTradeUnsettledCash),
    canCreditPlatformAccount: Boolean(data.settings?.canCreditPlatformAccount),
    canDebitPlatformAccounts: Boolean(data.settings?.canDebitPlatformAccounts),
  };
}

export function formValuesToProgramBooksControls(values: ProgramControlsBooksFormValues): IxcPortfolioV1BooksControls {
  return {
    settings: {
      canCreditCustomers: values.canCreditCustomers,
      canDebitCustomers: values.canDebitCustomers,
      canTradeUnsettledCash: values.canTradeUnsettledCash,
      canCreditPlatformAccount: values.canCreditPlatformAccount,
      canDebitPlatformAccounts: values.canDebitPlatformAccounts,
    },
  };
}

interface ProgramBooksControlsFormProps {
  lens: Lens<ProgramControlsBooksFormValues>;
}

export function ProgramBooksControlsForm({ lens }: ProgramBooksControlsFormProps) {
  return (
    <AccordionFormGroup icon={<LedgerIcon aria-hidden />} title="Books">
      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Credit Customers" lens={lens.focus('canCreditCustomers')} />}
      />

      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Debit Customers" lens={lens.focus('canDebitCustomers')} />}
      />

      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Trade Unsettled Cash" lens={lens.focus('canTradeUnsettledCash')} />}
      />

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Credit Platform Account" lens={lens.focus('canCreditPlatformAccount')} />
        }
      />

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Debit Platform Accounts" lens={lens.focus('canDebitPlatformAccounts')} />
        }
      />
    </AccordionFormGroup>
  );
}
